// FA-2: the airport pickup rules, shared by the booking form, the trip cards, the Admin settings card and the AI Attendant.
// SQL twins (migration 237): public.fa2_round_up_minutes, public.fa2_arrival_pickup, public.fa2_settings_for_org.
//
// The Ride ATL arrival rule (binding default; every number is a tenant Admin setting):
//   pickup = the flight's SCHEDULED arrival + 30 min (Domestic) or + 45 min (International), rounded UP to the next 5 minutes.
// Live data never silently moves a confirmed pickup: a change becomes a suggestion dispatch approves (unless Auto-apply is on).
import { parseFlightNumber } from './flightNumber.js';

export const DEFAULT_FLIGHT_SETTINGS = Object.freeze({
    arrival_buffer_domestic_minutes: 30,
    arrival_buffer_international_minutes: 45,
    pickup_rounding_minutes: 5,
    auto_apply_flight_changes: false,
    change_threshold_minutes: 10,
    poll_window_hours: 6,
    monthly_call_cap: 3000,
    client_delay_text_enabled: false,
    wait_start_domestic_minutes: 30,
    wait_start_international_minutes: 45,
});

export function flightSettings(settings) {
    return { ...DEFAULT_FLIGHT_SETTINGS, ...Object.fromEntries(Object.entries(settings || {}).filter(([, value]) => value !== null && value !== undefined)) };
}

const toDate = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (value === null || value === undefined || value === '') return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** Rounds UP to the next multiple of `step` minutes (an exact multiple stays; any seconds past it count as later). */
export function roundUpMinutes(value, step = 5) {
    const date = toDate(value);
    if (!date) return null;
    const size = Math.max(1, Number(step) || 5) * 60000;
    return new Date(Math.ceil(date.getTime() / size) * size);
}

/** The pickup for an arriving flight. Unknown terminal type uses the Domestic buffer (the card says it was not known). */
export function arrivalPickup(scheduledArrival, terminalType, settings) {
    const rules = flightSettings(settings);
    const arrival = toDate(scheduledArrival);
    if (!arrival) return null;
    const buffer = terminalType === 'international' ? rules.arrival_buffer_international_minutes : rules.arrival_buffer_domestic_minutes;
    return roundUpMinutes(new Date(arrival.getTime() + Number(buffer) * 60000), rules.pickup_rounding_minutes);
}

/** When the airport wait clock starts (stored for billing later; nothing charges on it in FA-2). */
export function waitTimeStart(gateArrival, terminalType, settings) {
    const rules = flightSettings(settings);
    const gate = toDate(gateArrival);
    if (!gate) return null;
    const minutes = terminalType === 'international' ? rules.wait_start_international_minutes : rules.wait_start_domestic_minutes;
    return new Date(gate.getTime() + Number(minutes) * 60000);
}

// ---------------------------------------------------------------------------------------------
// Capture helpers
// ---------------------------------------------------------------------------------------------
const AIRPORT_NAMES = [
    [/\b(?:hartsfield|jackson atlanta|atlanta (?:international )?airport|atl airport)\b/i, 'ATL'],
];
const KNOWN_AIRPORT_CODES = new Set(['ATL', 'PDK', 'FTY', 'RYY', 'LZU', 'JFK', 'LGA', 'EWR', 'LAX', 'ORD', 'MDW', 'DFW', 'IAH', 'MIA', 'FLL', 'MCO', 'CLT', 'BOS', 'DCA', 'IAD', 'BWI', 'SFO', 'SEA', 'DEN', 'PHX', 'LAS', 'MSP', 'DTW', 'PHL', 'BNA', 'SAV', 'CHS', 'TPA']);

/** The airport a pickup or drop-off address names (IATA code), or null. "ATL", "(ATL)", "Hartsfield-Jackson" -> ATL. */
export function detectAirport(text) {
    const value = String(text ?? '');
    if (!value.trim()) return null;
    for (const [pattern, code] of AIRPORT_NAMES) if (pattern.test(value)) return code;
    const coded = value.match(/\(([A-Z]{3})\)/)?.[1];
    if (coded) return coded;
    if (/\batl\b/i.test(value)) return 'ATL';
    // other codes only when written in capitals ("LAS", not "Las Palmas St")
    const words = value.match(/\b[A-Z]{3}\b/g) || [];
    return words.find((word) => KNOWN_AIRPORT_CODES.has(word)) || null;
}

/** Arriving when the trip starts at the airport, departing when it ends there. */
export function inferFlightDirection({ pickup = '', dropoff = '' } = {}) {
    const from = detectAirport(pickup);
    const to = detectAirport(dropoff);
    if (from && !to) return { direction: 'arriving', airportCode: from };
    if (to && !from) return { direction: 'departing', airportCode: to };
    if (from && to) return { direction: 'arriving', airportCode: from };
    return { direction: null, airportCode: null };
}

/** "international" / "domestic" from words a client or dispatcher used, else null. */
export function terminalTypeFromText(text) {
    // an airport's own name ("Hartsfield-Jackson Atlanta International Airport") says nothing about the terminal
    const value = String(text ?? '').replace(/\binternational\s+airport\b/gi, ' ');
    if (/\b(?:international|int'?l|concourse f|terminal f|maynard)\b/i.test(value)) return 'international';
    if (/\b(?:domestic)\b/i.test(value)) return 'domestic';
    return null;
}

// US and US-territory ICAO prefixes clear customs before they land (Domestic terminal). Pre-cleared foreign airports (Canada,
// Dublin, Shannon, Caribbean preclearance) also arrive Domestic but are not knowable from the code alone: dispatch can set the
// type on the trip, and an inferred type is labelled as such on the card.
const DOMESTIC_ICAO = /^(K|PA|PH|PG|PJ|PW|TJ|TIS)/;

/** Terminal type inferred from the other end of the flight (ICAO code), for an ATL pickup/drop-off. */
export function inferTerminalType(otherEndIcao) {
    const code = String(otherEndIcao ?? '').trim().toUpperCase();
    if (code.length !== 4) return null;
    return DOMESTIC_ICAO.test(code) ? 'domestic' : 'international';
}

/**
 * The structured flight capture every booking path writes (public.fa2_capture_trip_flight). Null when no flight is known.
 * @param {{ flightInfo?: string, pickup?: string, dropoff?: string, direction?: string|null, airportCode?: string|null,
 *           terminalType?: string|null, airline?: string|null, scheduledTime?: string|null, notes?: string }} input
 */
export function buildFlightCapture({ flightInfo = '', pickup = '', dropoff = '', direction = null, airportCode = null,
    terminalType = null, airline = null, scheduledTime = null, notes = '' } = {}) {
    const parsed = parseFlightNumber(flightInfo);
    if (!parsed) return null;
    const inferred = inferFlightDirection({ pickup, dropoff });
    const finalDirection = ['arriving', 'departing'].includes(direction) ? direction : inferred.direction;
    const finalTerminal = ['domestic', 'international'].includes(terminalType) ? terminalType
        : terminalTypeFromText(`${flightInfo} ${notes} ${finalDirection === 'departing' ? dropoff : pickup}`);
    return {
        flight_number: parsed.flightNumber,
        airline: (airline && String(airline).trim().toUpperCase()) || parsed.airlineCode,
        direction: finalDirection,
        airport_code: (airportCode && String(airportCode).trim().toUpperCase()) || inferred.airportCode,
        terminal_type: finalTerminal,
        ...(scheduledTime ? { scheduled_time: scheduledTime } : {}),
    };
}

// ---------------------------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------------------------
export function formatClock(value, timeZone) {
    const date = toDate(value);
    if (!date) return '';
    try {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', ...(timeZone ? { timeZone } : {}) }).format(date);
    } catch {
        return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
    }
}

/** One line for the trip card: "Flight delayed 40 min · suggested pickup 4:15 PM". Null when there is nothing waiting. */
export function suggestionLine(detail, { timeZone } = {}) {
    if (!detail || detail.flight_suggestion_status !== 'pending' || !detail.flight_suggested_pickup_at) return null;
    const minutes = Math.abs(Number(detail.flight_suggestion_change_minutes) || 0);
    const what = detail.flight_suggestion_reason === 'early' ? `Flight early ${minutes} min`
        : detail.flight_suggestion_reason === 'rescheduled' ? `Flight time changed ${minutes} min`
            : `Flight delayed ${minutes} min`;
    return `${what} · suggested pickup ${formatClock(detail.flight_suggested_pickup_at, timeZone)}`;
}

/** Short status for the card: "DL30 · arriving ATL · Domestic · Scheduled". */
export function flightSummary(detail) {
    if (!detail?.flight_number) return null;
    const parts = [detail.flight_number];
    if (detail.flight_direction) parts.push(`${detail.flight_direction}${detail.airport_code ? ` ${detail.airport_code}` : ''}`);
    if (detail.flight_terminal_type) parts.push(`${detail.flight_terminal_type === 'international' ? 'International' : 'Domestic'}${detail.flight_terminal_type_source === 'inferred' ? ' (from FlightAware)' : ''}`);
    else if (detail.flight_direction === 'arriving') parts.push('terminal not known');
    if (detail.flight_status) parts.push(detail.flight_status);
    return parts.join(' · ');
}
